"use client";

import React, { useState, useRef, useEffect } from "react";
import {
	Menu,
	LogOut,
	Clock,
	CreditCard,
	Settings,
	FolderOpen,
	LayoutDashboard,
	MessageSquare,
	Globe,
} from "lucide-react";
import { useUser } from "@/context/UserProvider";
import { useLocale, useTranslations } from "next-intl";
import { Link, usePathname, useRouter } from "@/i18n/navigation";
import { useClickAway } from "react-use";

const dashboardLink = { id: "dashboard", icon: LayoutDashboard, href: "/studio/dashboard", tKey: "dashboard" };

export default function UserMenu() {
	const t = useTranslations("UserMenu");
	const locale = useLocale();
	const pathname = usePathname();
	const { userId, credits, logout } = useUser();
	const router = useRouter();
	const [isOpen, setIsOpen] = useState(false);
	const menuRef = useRef<HTMLDivElement>(null);

	const toggleLocale = () => {
		const nextLocale = locale === 'ru' ? 'en' : 'ru';
		router.replace(pathname, { locale: nextLocale });
	};

	useClickAway(menuRef, () => {
		if (isOpen) setIsOpen(false);
	}, ["mousedown", "touchstart"]);

	useEffect(() => {
		if (!isOpen) return;
		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === "Escape") setIsOpen(false);
		};
		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [isOpen]);

	const handleLogout = async () => {
		setIsOpen(false);
		await logout();
		router.replace("/");
	};

	const handleOpenStudioSettings = () => {
		setIsOpen(false);
		window.dispatchEvent(new CustomEvent("openStudioSettings"));
	};

	// Check if user is on a studio page (where settings are available)
	const isStudioPage = pathname.startsWith("/studio/") && !pathname.includes("/admin_dashboard");

	return (
		<div className="relative" ref={menuRef}>
			<button
				onClick={() => setIsOpen((prev) => !prev)}
				className="w-[48px] h-[48px] rounded-lg bg-secondary flex items-center justify-center hover:bg-secondary/80 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-kino-accent cursor-pointer"
				aria-label={t("ariaOpen")}
				aria-expanded={isOpen}
			>
				<Menu size={22} className="text-foreground" />
			</button>

			{isOpen && (
				<div className="absolute right-0 top-full mt-2 w-72 max-w-[calc(100vw-2*var(--safe-margin))] bg-card border border-white/10 rounded-xl shadow-2xl py-1 z-[80] animate-in fade-in slide-in-from-top-2 duration-200 origin-top-right">
					{/* Block 1: Main Navigation */}
					<div className="py-1">
						{/* Главная */}
						<Link
							href={dashboardLink.href}
							className="w-full text-left px-4 py-3 text-sm text-foreground hover:bg-white/5 flex items-center gap-3 min-h-[44px] cursor-pointer"
							onClick={() => setIsOpen(false)}
						>
							<dashboardLink.icon size={18} />
							{t(dashboardLink.tKey)}
						</Link>

						{/* Настройки генерации - only on studio pages */}
						{isStudioPage && (
							<button
								type="button"
								onClick={handleOpenStudioSettings}
								className="w-full text-left px-4 py-3 text-sm text-foreground hover:bg-white/5 flex items-center gap-3 min-h-[44px] cursor-pointer"
							>
								<Settings size={18} />
								{t("studio_settings")}
							</button>
						)}

						{/* История генераций */}
						<Link
							href="/studio/history"
							className="w-full text-left px-4 py-3 text-sm text-foreground hover:bg-white/5 flex items-center gap-3 min-h-[44px] cursor-pointer"
							onClick={() => setIsOpen(false)}
						>
							<FolderOpen size={18} />
							{t("folders")}
						</Link>

						{/* Пополнить токены */}
						<Link
							href="/billing/tokens"
							className="w-full text-left px-4 py-3 text-sm text-foreground hover:bg-white/5 flex items-center gap-3 min-h-[44px] cursor-pointer"
							onClick={() => setIsOpen(false)}
						>
							<CreditCard size={18} />
							{t("tokens")}
							<span className="ml-auto text-xs font-mono font-bold text-kino-accent">{credits}</span>
						</Link>
					</div>

					{/* Block 2: User Divider */}
					<div className="border-t border-white/10 my-1" />
					<div className="px-4 py-2">
						<p className="text-sm font-medium text-foreground">{t("user")}</p>
						<p className="text-xs text-muted-foreground truncate">{userId || "user@example.com"}</p>
					</div>
					<div className="border-t border-white/10 my-1" />

					{/* Block 3: Settings & Language */}
					<div className="py-1">
						{/* Выбор языка */}
						<button
							type="button"
							onClick={() => {
								toggleLocale();
								setIsOpen(false);
							}}
							className="w-full text-left px-4 py-3 text-sm text-foreground hover:bg-white/5 flex items-center gap-3 min-h-[44px] cursor-pointer"
							title={locale === "ru" ? "Switch to English" : "Переключить на Русский"}
						>
							<Globe size={18} />
							{locale === "ru" ? "English" : "Русский"}
						</button>

						{/* Настройки аккаунта */}
						<Link
							href="/account/settings"
							className="w-full text-left px-4 py-3 text-sm text-foreground hover:bg-white/5 flex items-center gap-3 min-h-[44px] cursor-pointer"
							onClick={() => setIsOpen(false)}
						>
							<Settings size={18} />
							{t("account")}
						</Link>

						{/* История платежей */}
						<Link
							href="/billing/history"
							className="w-full text-left px-4 py-3 text-sm text-foreground hover:bg-white/5 flex items-center gap-3 min-h-[44px] cursor-pointer"
							onClick={() => setIsOpen(false)}
						>
							<Clock size={18} />
							{t("payments")}
						</Link>

						{/* Поддержка */}
						<Link
							href="/contact"
							className="w-full text-left px-4 py-3 text-sm text-foreground hover:bg-white/5 flex items-center gap-3 min-h-[44px] cursor-pointer"
							onClick={() => setIsOpen(false)}
						>
							<MessageSquare size={18} className="text-kino-accent" />
							{t("support")}
						</Link>
					</div>

					{/* Block 4: Logout */}
					<div className="border-t border-white/10 my-1"></div>
					<button
						type="button"
						onClick={handleLogout}
						className="w-full text-left px-4 py-3 text-sm text-red-500 hover:bg-red-500/10 flex items-center gap-3 min-h-[44px] cursor-pointer"
					>
						<LogOut size={18} />
						{t("logout")}
					</button>
				</div>
			)}
		</div>
	);
}
